import { sendUserApproveRejectQuote } from "@/models/socket/quotes.model";
import { Button } from "@/shadcn/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shadcn/components/ui/dialog";
import { FullSingleQuote } from "@/types";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import React, { useState } from "react";

type QuoteRejectDialogProps = {
  quoteDbID: string;
  quoteDisplayID: string;
  updateQuoteStatus: (newStatus: FullSingleQuote["quote_status"]) => void;
};

const QuoteRejectDialog: React.FC<QuoteRejectDialogProps> = ({
  quoteDbID,
  quoteDisplayID,
  updateQuoteStatus,
}) => {
  const [rejectReason, setRejectReason] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<boolean>(false); // controls dialog open state so we can close it programmatically

  async function rejectQuote() {
    // controlling dialog open state allows us to close it after success
    console.log("Quote rejected:", quoteDbID, quoteDisplayID);
    if (!rejectReason.trim()) {
      setError("A rejection reason is required.");
      return;
    }

    if (rejectReason.length > 255) {
      setError("Rejection reason cannot exceed 255 characters.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const response = await sendUserApproveRejectQuote(
      quoteDbID,
      "reject",
      rejectReason.trim()
    );

    if (response.rStatus === "success") {
      genericSuccessToast("Quote rejected saved");
      console.log("Quote rejected successfully");
      setOpen(false); // close dialog on success to improve UX
      updateQuoteStatus("rejected"); // update status in parent component
    } else {
      genericFailureToast("Failed to reject quote: " + response.rData);
      console.error("Error rejecting quote:", response.rData);
    }

    setSubmitting(false);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open); // keep open state in sync so we can close dialog programmatically
        if (!open) {
          setTimeout(() => {
            setRejectReason("");
            setError(null);
            setSubmitting(false);
          }, 100); // Reset state after dialog closes
        }
      }}
    >
      <DialogTrigger asChild>
        <Button className="bg-red-500 text-white hover:bg-red-500/90">
          Reject
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Reject Quote: {quoteDisplayID}</DialogTitle>
          <DialogDescription></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p>
            Are you sure you want to reject the quote? <br />
            Please provide a reason for rejection.
          </p>
          {error && <div className="text-sm text-red-500">{error}</div>}
          <textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter rejection reason"
            className="w-full rounded border p-2"
            rows={4}
          />
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            className="bg-red-500 text-white hover:bg-red-500/90"
            onClick={() => {
              rejectQuote();
            }}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Reject Quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuoteRejectDialog;
